// Calendar utility functions for adding direct debits to calendars

import { getCurrencySymbol } from './currency';

const REMINDER_LABELS = { 7: '7 days', 5: '5 days', 1: '1 day' };

/**
 * Generate calendar event details from a direct debit
 */
export function generateEventDetails(debit, reminderDays = [7, 5, 1]) {
  const title = `Direct Debit: ${debit.company_name}`;
  const symbol = getCurrencySymbol(debit.currency);
  const reminderText = reminderDays.length > 0
    ? `\n\nReminder: Set alerts for ${reminderDays.map(d => REMINDER_LABELS[d] || `${d} days`).join(', ')} before this payment.`
    : '';
  const description = `Amount: ${symbol}${debit.amount.toFixed(2)}\\nFrequency: ${debit.frequency}\\n${debit.notes ? `Notes: ${debit.notes}` : ''}${reminderText}\\n\\nManaged by My Direct Debits`;
  const amount = debit.amount;
  
  // Calculate the next payment date
  const nextDate = new Date(debit.next_payment_date);
  
  // For calendar events, we'll set it as an all-day event on the payment day
  const startDate = formatDateForCalendar(nextDate);
  const endDate = formatDateForCalendar(new Date(nextDate.getTime() + 24 * 60 * 60 * 1000));
  
  return { title, description, startDate, endDate, amount };
}

/**
 * Format date as YYYYMMDD for calendar URLs
 */
function formatDateForCalendar(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

/**
 * Format date for .ics file (YYYYMMDD format)
 */
function formatDateForICS(date) {
  return formatDateForCalendar(date);
}

/**
 * Get recurrence rule based on frequency
 */
function getRecurrenceRule(frequency) {
  switch (frequency) {
    case 'weekly':
      return 'RRULE:FREQ=WEEKLY';
    case 'bi-weekly':
      return 'RRULE:FREQ=WEEKLY;INTERVAL=2';
    case 'monthly':
      return 'RRULE:FREQ=MONTHLY';
    case 'quarterly':
      return 'RRULE:FREQ=MONTHLY;INTERVAL=3';
    case 'six-monthly':
      return 'RRULE:FREQ=MONTHLY;INTERVAL=6';
    case 'annually':
      return 'RRULE:FREQ=YEARLY';
    default:
      return 'RRULE:FREQ=MONTHLY';
  }
}

/**
 * Generate Google Calendar URL
 */
export function generateGoogleCalendarUrl(debit, reminderDays = [7, 5, 1]) {
  const { title, description, startDate, endDate } = generateEventDetails(debit, reminderDays);
  const recurrence = getRecurrenceRule(debit.frequency);
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${startDate}/${endDate}`,
    details: description.replace(/\\n/g, '\n'),
    recur: recurrence,
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate Yahoo Calendar URL
 */
export function generateYahooCalendarUrl(debit, reminderDays = [7, 5, 1]) {
  const { title, description, startDate } = generateEventDetails(debit, reminderDays);
  
  // Yahoo uses different duration format
  const durHours = '24'; // All day event
  
  const params = new URLSearchParams({
    v: '60',
    title: title,
    st: startDate,
    dur: durHours,
    desc: description.replace(/\\n/g, '\n'),
  });
  
  return `https://calendar.yahoo.com/?${params.toString()}`;
}

/**
 * Generate Outlook Web URL
 */
export function generateOutlookUrl(debit, reminderDays = [7, 5, 1]) {
  const { title, description, startDate, endDate } = generateEventDetails(debit, reminderDays);
  
  const start = `${startDate.slice(0,4)}-${startDate.slice(4,6)}-${startDate.slice(6,8)}`;
  const end = `${endDate.slice(0,4)}-${endDate.slice(4,6)}-${endDate.slice(6,8)}`;
  
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: title,
    body: description.replace(/\\n/g, '\n'),
    startdt: start,
    enddt: end,
    allday: 'true',
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Generate .ics file content for download (works with Apple Calendar, Outlook desktop)
 */
export function generateICSContent(debit, reminderDays = [7, 5, 1]) {
  const { title, description, startDate, endDate } = generateEventDetails(debit, reminderDays);
  const recurrence = getRecurrenceRule(debit.frequency);
  const uid = `${debit.id}@mydirectdebits.app`;
  const now = new Date();
  const timestamp = formatDateForICS(now) + 'T' + 
    String(now.getHours()).padStart(2, '0') +
    String(now.getMinutes()).padStart(2, '0') +
    String(now.getSeconds()).padStart(2, '0') + 'Z';
  
  const valarms = reminderDays.map(days => {
    const label = days === 1 ? `${title} is due tomorrow` : `${title} is due in ${days} days`;
    return `BEGIN:VALARM
TRIGGER:-P${days}D
ACTION:DISPLAY
DESCRIPTION:${label}
END:VALARM`;
  }).join('\n');

  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//My Direct Debits//Reminder App//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${timestamp}
DTSTART;VALUE=DATE:${startDate}
DTEND;VALUE=DATE:${endDate}
${recurrence}
SUMMARY:${title}
DESCRIPTION:${description.replace(/\\n/g, '\\n')}
STATUS:CONFIRMED
TRANSP:TRANSPARENT
${valarms}
END:VEVENT
END:VCALENDAR`;

  return icsContent;
}

/**
 * Download .ics file
 */
export function downloadICSFile(debit, reminderDays = [7, 5, 1]) {
  const icsContent = generateICSContent(debit, reminderDays);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${debit.company_name.replace(/[^a-z0-9]/gi, '_')}_reminder.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
